import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt as Send, Envelope as Mail, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import useUserState, { getStoredEmail } from "../hooks/useUserState";
import { RelayLoader } from "../components/RelayLoader";
import { homeSection } from '../lib/marketingHome';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "#FAF7F0",
  ink: "#2A1E5C",
  ink2: "#5D5A6B",
  muted: "#8B7E6A",
  line: "#E8E2D5",
  card: "#FFFFFF",
  gold: "#F5CB5C",
  bubbleUser: "#2A1E5C",
  bubbleCoach: "#FFF3C6",
};

const GREETING = {
  role: "assistant",
  content:
    "Hey, I'm Caddy, your Payday Relay Coach 🏁. I can help with anything in the system: setting up your Legend, running weekly relays, paying yourself, or making sense of your Scoreboard. What's on your mind?",
};

const SUGGESTIONS = [
  "How do I set up my LEGEND tab?",
  "When can I actually pay myself?",
  "What's a Payday Rhythm?",
  "My weekly target looks wrong",
];

export default function SupportPage() {
  const { state, loading } = useUserState();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateEmail, setEscalateEmail] = useState(() => getStoredEmail() || "");
  const [escalateNote, setEscalateNote] = useState("");
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const bottomRef = useRef(null);

  const isPayingCustomer = !!(state && (
    state.has_payday_relay || state.has_four_relay || state.has_income_map_purchase || state.has_pit_crew
  ));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Auto-seed the first message from ?q=… (used by "Ask the Coach about this" links)
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (loading) return;           // wait for auth state
    if (!isPayingCustomer) return; // splash gate is showing, don't consume the ?q
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("q") || "").trim();
    if (!q) return;
    seededRef.current = true;
    // Strip the query so a refresh doesn't loop it
    try { window.history.replaceState({}, "", window.location.pathname); } catch { /* noop */ }
    // small delay so the greeting renders first
    setTimeout(() => { send(q); }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isPayingCustomer]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;

    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(`${API}/api/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "The Coach hit a snag, try again in a moment.");
        setMessages((m) => m.slice(0, -1)); // roll back the user msg
        return;
      }
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Network hiccup, try again.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!sessionId) {
      toast.error("Ask Caddy at least one question first.");
      return;
    }
    setEscalating(true);
    try {
      const res = await fetch(`${API}/api/support/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          email: escalateEmail || null,
          note: escalateNote || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Couldn't send the escalation.");
        return;
      }
      setEscalated(true);
      toast.success("Sent to support, we'll get back to you soon.");
    } catch {
      toast.error("Network hiccup, try again in a minute.");
    } finally {
      setEscalating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: COLOR.bg }}>
      {/* Header */}
      <header className="py-2 px-4 sm:px-6 border-b" style={{ borderColor: COLOR.line, background: COLOR.card }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="support-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div className="flex items-center gap-2">
            <img src="/images/paydayrelaylogo-tight.png" alt="Payday Relay" className="h-[64px] sm:h-[81px] w-auto" data-testid="support-header-logo" />
          </div>
        </div>
      </header>

      {!loading && !isPayingCustomer ? (
        <main className="flex-1 flex items-center justify-center px-6 py-12" data-testid="support-locked-gate">
          <div className="max-w-md w-full text-center">
            <h1 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Meet your Payday Relay Coach
            </h1>
            <p className="text-sm leading-relaxed mb-8" style={{ color: COLOR.ink2 }}>
              The Coach is here 24/7 for everyone running the Payday Relay system, setup help, weekly relays,
              Scoreboard questions, all of it. Start your Free Sprint or grab a Season Pass, and the Relay
              Calculator, your Google Sheet, and the Coach all come with it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start/free-sprint"
                className="inline-block px-8 py-3.5 rounded-full font-bold text-base transition-transform hover:scale-105"
                style={{ background: COLOR.gold, color: COLOR.ink }}
                data-testid="support-locked-free-sprint"
              >
                Start my Free Sprint →
              </Link>
              <Link
                to={homeSection('season-pass')}
                className="inline-block px-8 py-3.5 rounded-full font-semibold text-base border transition-transform hover:scale-105"
                style={{ borderColor: COLOR.line, color: COLOR.ink, background: COLOR.card }}
                data-testid="support-locked-season-pass"
              >
                Get Payday Relay
              </Link>
            </div>
            <p className="text-xs mt-6" style={{ color: COLOR.muted }}>
              Already a member?{" "}
              <Link to="/login" className="underline" style={{ color: COLOR.ink }} data-testid="support-locked-signin">
                Sign in with your email →
              </Link>
            </p>
          </div>
        </main>
      ) : (
        <>
          {/* Coach intro */}
          <section className="px-4 sm:px-6 pt-8 pb-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3" style={{ background: "#FFF3C6", color: "#7A5E10" }}>
            🏁 Ask Caddy
          </div>
          <h1 className="text-3xl sm:text-4xl font-medium mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Caddy, your Payday Relay Coach
          </h1>
          <p className="text-sm max-w-md mx-auto" style={{ color: COLOR.ink2 }}>
            Ask me anything about the guide. If I can't answer confidently, I'll pass it straight to our support team.
          </p>
        </div>
      </section>

      {/* Chat feed */}
      <main className="flex-1 overflow-y-auto px-4 sm:px-6 pb-32">
        <div className="max-w-3xl mx-auto space-y-4 pt-2" data-testid="chat-feed">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : i === 0 ? "justify-center" : "justify-start"}`}
              data-testid={`msg-${m.role}-${i}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${i === 0 ? "text-center" : ""}`}
                style={{
                  background: m.role === "user" ? COLOR.bubbleUser : COLOR.bubbleCoach,
                  color: m.role === "user" ? "#fff" : COLOR.ink,
                  borderBottomRightRadius: m.role === "user" ? "6px" : "16px",
                  borderBottomLeftRadius: m.role === "user" ? "16px" : "6px",
                }}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="coach-typing">
              <div
                className="px-3 py-2 rounded-2xl inline-flex items-center"
                style={{ background: COLOR.bubbleCoach, color: COLOR.ink }}
              >
                <RelayLoader label={null} size="sm" />
              </div>
            </div>
          )}

          {/* Quick suggestion chips (only before first user message) */}
          {messages.length === 1 && !sending && (
            <div className="pt-4 flex flex-wrap gap-2" data-testid="quick-suggestions">
              {SUGGESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="px-3 py-2 rounded-full text-xs font-medium border transition-transform hover:scale-105"
                  style={{ borderColor: COLOR.line, background: COLOR.card, color: COLOR.ink2 }}
                  data-testid={`suggestion-${q.slice(0, 20).replace(/\W/g, '-')}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </main>

      {/* Input + escalate footer */}
      <footer className="fixed bottom-0 left-0 right-0 border-t px-4 sm:px-6 py-3" style={{ background: COLOR.card, borderColor: COLOR.line }}>
        <div className="max-w-3xl mx-auto">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Caddy…"
              maxLength={2000}
              className="flex-1 px-4 py-3 rounded-full border text-sm focus:outline-none"
              style={{ borderColor: COLOR.line, background: COLOR.bg, color: COLOR.ink }}
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="p-3 rounded-full transition-transform hover:scale-105 disabled:opacity-40"
              style={{ background: COLOR.gold, color: COLOR.ink }}
              data-testid="chat-send-btn"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="text-center mt-2">
            <button
              onClick={() => setShowEscalate(true)}
              disabled={!sessionId}
              className="text-xs underline disabled:opacity-40"
              style={{ color: COLOR.muted }}
              data-testid="escalate-btn"
            >
              This isn't quite right, contact support
            </button>
          </div>
        </div>
      </footer>
        </>
      )}

      {/* Escalate modal */}
      {showEscalate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(42, 30, 92, 0.55)" }}
          data-testid="escalate-modal"
          onClick={() => !escalating && setShowEscalate(false)}
        >
          <div
            className="max-w-md w-full rounded-2xl p-6"
            style={{ background: COLOR.card }}
            onClick={(e) => e.stopPropagation()}
          >
            {escalated ? (
              <div className="text-center py-4" data-testid="escalate-success">
                <h3 className="text-xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  Sent to support
                </h3>
                <p className="text-sm" style={{ color: COLOR.ink2 }}>
                  We'll follow up at {escalateEmail || "the address you gave"} as soon as we can. In the meantime, keep running your relay 🏁
                </p>
                <button
                  onClick={() => setShowEscalate(false)}
                  className="mt-5 px-6 py-2 rounded-full font-semibold text-sm"
                  style={{ background: COLOR.ink, color: "#fff" }}
                  data-testid="escalate-close"
                >
                  Back to chat
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  Send this to support
                </h3>
                <p className="text-sm mb-5" style={{ color: COLOR.ink2 }}>
                  I'll forward the whole conversation to our support team at support@paydayrelay.com. Add your email so we can reply.
                </p>
                <label className="block text-xs font-semibold mb-1" style={{ color: COLOR.ink }}>Your email</label>
                <input
                  type="email"
                  value={escalateEmail}
                  onChange={(e) => setEscalateEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 rounded-lg border text-sm mb-4"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="escalate-email-input"
                />
                <label className="block text-xs font-semibold mb-1" style={{ color: COLOR.ink }}>Anything else? (optional)</label>
                <textarea
                  value={escalateNote}
                  onChange={(e) => setEscalateNote(e.target.value)}
                  placeholder="Any extra context for support…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border text-sm mb-5"
                  style={{ borderColor: COLOR.line, color: COLOR.ink }}
                  data-testid="escalate-note-input"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowEscalate(false)}
                    disabled={escalating}
                    className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm border"
                    style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "#fff" }}
                    data-testid="escalate-cancel"
                  >
                    Never mind
                  </button>
                  <button
                    onClick={escalate}
                    disabled={escalating || !escalateEmail.includes("@")}
                    className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: COLOR.ink, color: "#fff" }}
                    data-testid="escalate-send"
                  >
                    <Mail className="w-4 h-4" />
                    {escalating ? "Sending…" : "Send to support"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin: 0 1px;
          background: ${COLOR.muted};
          border-radius: 50%;
          animation: typing 1.2s infinite ease-in-out;
        }
        .dot:nth-child(2) { animation-delay: 0.15s; }
        .dot:nth-child(3) { animation-delay: 0.3s; }
        @keyframes typing {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </div>
  );
}
